import { Box, Link as MuiLink, Tooltip, Typography, useTheme } from '@mui/material'
import {
  Person as PersonIcon,
  Groups as GroupsIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ROUTES } from '@/utils/constants'
import { formatLessonClock } from '@/utils/date'

interface Participant {
  user?: { display_name?: string } | null
}

interface TimelineLesson {
  id: number
  planned_start_at: string | null
  status: string
  lesson_scope: 'individual' | 'group' | string
  was_rescheduled?: boolean
  last_reschedule_reason?: string | null
  participants?: Participant[]
}

interface ScheduleTimelineProps {
  lessons: TimelineLesson[]
}

/** Цвет точки по статусу урока */
function useStatusColor() {
  const theme = useTheme()
  return (status: string): string =>
    ({
      planned: theme.palette.primary.main,
      in_progress: theme.palette.warning.main,
      completed: theme.palette.success.main,
      canceled: theme.palette.text.disabled,
    })[status] ?? theme.palette.primary.main
}

/**
 * Вертикальный таймлайн расписания: непрерывная линия слева с точками и
 * временем; справа — формат урока (иконка), имена участников и, если урок
 * переносили, иконка с комментарием переноса в поповере.
 */
export function ScheduleTimeline({ lessons }: ScheduleTimelineProps) {
  const { t } = useTranslation()
  const statusColor = useStatusColor()

  return (
    <Box>
      {lessons.map((lesson) => {
        const isGroup = lesson.lesson_scope === 'group'
        const names = (lesson.participants ?? [])
          .map((p) => p.user?.display_name)
          .filter(Boolean)
          .join(', ')
        const time = formatLessonClock(lesson.planned_start_at)
        const rescheduleTitle = lesson.last_reschedule_reason
          ? `${t('dashboard.widgets.today_schedule.rescheduled')}: ${lesson.last_reschedule_reason}`
          : t('dashboard.widgets.today_schedule.rescheduled')

        return (
          <Box key={lesson.id} sx={{ display: 'flex', alignItems: 'stretch', minHeight: 40 }}>
            {/* Линия + точка + время (слева) */}
            <Box
              sx={{
                position: 'relative',
                pr: 2,
                minWidth: 60,
                borderRight: '2px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                {time}
              </Typography>
              <Box
                sx={{
                  position: 'absolute',
                  right: '-6px',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: statusColor(lesson.status),
                  border: '2px solid',
                  borderColor: 'background.paper',
                }}
              />
            </Box>

            {/* Контент урока (справа от линии) */}
            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                pl: 2,
              }}
            >
              <Tooltip title={isGroup ? t('lessons.scopeGroup') : t('lessons.scopeIndividual')}>
                {isGroup ? (
                  <GroupsIcon fontSize="small" color="action" />
                ) : (
                  <PersonIcon fontSize="small" color="action" />
                )}
              </Tooltip>

              <MuiLink
                component={Link}
                to={ROUTES.LESSON_DETAILS(lesson.id)}
                variant="body2"
                noWrap
                title={names || undefined}
                sx={{ flexGrow: 1, minWidth: 0 }}
              >
                {names || `#${lesson.id}`}
              </MuiLink>

              {lesson.was_rescheduled && (
                <Tooltip title={rescheduleTitle} arrow>
                  <SwapHorizIcon fontSize="small" color="warning" sx={{ cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
